import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonIcon,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import PillButton from '../../../shared/PillButton';
import './ProjectModal.css';

/**
 * ProjectModal Component
 * Modal for creating or editing projects.
 *
 * `deployTemplate` (optional) — when present, the modal is in "create &
 * deploy" mode: the title, button label, and a small subtitle change to
 * reflect that the user is creating a project specifically to deploy a
 * marketplace template into it. The actual deploy is the parent's
 * responsibility; this modal only relays project creation as usual.
 *   { slug, name, icon }
 */
const ProjectModal = ({
  isOpen,
  onDismiss,
  onSubmit,
  initialProject = null,
  isSubmitting = false,
  deployTemplate = null,
}) => {
  const [projectName, setProjectName] = useState('');
  const isEditMode = !!initialProject;
  const isDeployMode = !isEditMode && !!deployTemplate;

  // Update form when initialProject changes. Default to the template
  // slug when in deploy mode so the user can hit Enter immediately.
  useEffect(() => {
    if (initialProject) {
      setProjectName(initialProject.name);
    } else if (deployTemplate?.slug) {
      setProjectName(deployTemplate.slug);
    } else {
      setProjectName('');
    }
  }, [initialProject, deployTemplate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (projectName.trim()) {
      onSubmit({ name: projectName.trim() });
    }
  };

  const handleClose = () => {
    setProjectName('');
    onDismiss();
  };

  return (
    <IonModal 
      isOpen={isOpen} 
      onDidDismiss={handleClose}
      className="project-modal"
    >
      <div className="project-modal-body">
        <div className="project-modal-header">
          <h2 className="project-modal-title">
            {isEditMode
              ? 'Edit Project'
              : isDeployMode
                ? 'Create project & deploy'
                : 'Create Project'}
          </h2>
          <button
            type="button"
            className="project-modal-close"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="project-modal-form">
          {isDeployMode && (
            <div className="project-modal-deploy-context">
              Creating a project to deploy{' '}
              <strong>{deployTemplate.name || deployTemplate.slug}</strong>{' '}
              into.
            </div>
          )}

          <div className="form-field">
            <label htmlFor="project-name">Project Name</label>
            <input
              id="project-name"
              type="text"
              className="mkt-field mkt-field--input"
              placeholder="Enter project name..."
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="modal-actions">
            <PillButton
              type="submit"
              loading={isSubmitting}
              disabled={!projectName.trim()}
            >
              {isEditMode
                ? 'Update Project'
                : isDeployMode
                  ? 'Create & Deploy'
                  : 'Create Project'}
            </PillButton>
          </div>
        </form>
      </div>
    </IonModal>
  );
};

export default ProjectModal;



